import type { FC } from "react";

import { usePageMetadata } from "../../hooks/usePageMetadata";
import { LandingModelsSection } from "./PublicLandingModels";
import {
  LandingAuthSection,
  LandingCapabilitiesSection,
  LandingDifferentiatorsSection,
  LandingHeroSection,
  LandingLifecycleSection,
} from "./PublicLandingSections";
import {
  LandingEnvironmentSection,
  LandingParallelSection,
  LandingRosterSection,
} from "./PublicLandingShowcase";
import { LandingSkillMeshSection } from "./PublicLandingSkillMesh";
import { PublicPageShell } from "./PublicPageShell";

const LANDING_NAV_ITEMS = [
  { href: "#models", label: "Models" },
  { href: "#actors", label: "Actors" },
  { href: "#skill-mesh", label: "Skill Mesh" },
  { href: "#lifecycle", label: "How it works" },
  { href: "/billing", label: "Pricing" },
] as const;

export const PublicLandingPage: FC = () => {
  usePageMetadata({
    description:
      "Arti actors are persistent AI teammates on Claude Opus 5.5, Claude Fable 5.1, GPT-6 Astra and GPT-6 Sol: they live in your channels, remember months of work, run sandboxed dev environments, and cost nothing while parked.",
    keywords:
      "AI teammates, persistent AI agents, actor system, durable agent memory, Claude Opus 5.5, GPT-6, MCP server, shared AI skills, sandboxed AI environments, multi-agent collaboration, AI engineering platform",
    robots:
      "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      applicationCategory: "BusinessApplication",
      description:
        "Arti's actor system gives teams persistent AI members with durable memory, sandboxed dev environments, channel-native collaboration, and zero idle cost.",
      featureList: [
        "Persistent actor identity from reusable templates",
        "Per-actor model choice: Claude Opus 5.5, Claude Fable 5.1, GPT-6 Astra, GPT-6 Sol and more",
        "Durable two-layer memory with self-consolidation",
        "Park/wake lifecycle with zero idle cost",
        "Sandboxed per-channel dev environments with live preview URLs",
        "Parallel feature delivery: one channel, team and preview per feature",
        "Shared channels with @mention collaboration",
        "Glass-box timeline of every wake and decision",
        "Skill Mesh: one versioned set of company skills for Claude and ChatGPT over MCP",
      ],
      name: "Arti",
      operatingSystem: "Web",
      url: "/",
    },
    title: "Arti | AI Teammates That Remember — The Actor System",
    type: "website",
  });

  return (
    <PublicPageShell
      navItems={LANDING_NAV_ITEMS}
      primaryActionHref="#auth"
      primaryActionLabel="Request access"
    >
      <LandingHeroSection />
      <LandingModelsSection />
      <LandingRosterSection />
      <LandingSkillMeshSection />
      <LandingLifecycleSection />
      <LandingEnvironmentSection />
      <LandingParallelSection />
      <LandingCapabilitiesSection />
      <LandingDifferentiatorsSection />
      <LandingAuthSection />
    </PublicPageShell>
  );
};
